import './TopicShare.scss';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import CustomBarChart from '../../../../shared/components/chart/custom-bar-chart/CustomBarChart';
import { prepareAndGetVerticalBarOptions } from '../../../../shared/components/chart/custom-bar-chart/helpers/CustomBarChartHelper';
import { renderToStaticMarkup } from 'react-dom/server';
import ChartTooltip from '../../../../shared/components/chart/custom-bar-chart/components/chart-tooltip/ChartTooltip';
import { useChartColorPalette } from '../../../../shared/components/chart/custom-bar-chart/hooks/UseChartColorPalette';
import { useLegendState } from '../../../../shared/components/chart/custom-bar-chart/hooks/UseLegendState';
import { TopicsDistribution } from '../../../../shared/models/context-insights/ContextInsightsResponse';
import SimpleBar from 'simplebar-react';
import { SuggestedTopic } from '../../../../shared/models/projects/ProjectResponse';

interface TopicShareProps {
  topicsDistribution: TopicsDistribution[];
  suggestedTopics: SuggestedTopic[] | [];
}

const TopicShare: React.FC<TopicShareProps> = ({ topicsDistribution, suggestedTopics }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'en' | 'pl';

  const typesShareMatrix = useMemo(() => {
    if (!topicsDistribution?.length) return [];
    return [
      ['topic', 'value'],
      ...topicsDistribution
        .sort((a, b) => b.percentage - a.percentage)
        .map((t) => [
          suggestedTopics.find((st) => st.name.toLowerCase().trim() === t.topic.toLowerCase().trim())?.[
            currentLang === 'pl' ? 'namePl' : 'name'
          ] || t.topic,

          t.percentage.toFixed(1),
        ]),
    ];
  }, [topicsDistribution]);

  const initialLabels = useMemo(
    () =>
      typesShareMatrix!.map(
        (d) =>
          suggestedTopics.find((st) => st.name.toLowerCase().trim() === d[0].toLowerCase().trim())?.[
            currentLang === 'pl' ? 'namePl' : 'name'
          ] || d[0]
      ),
    [typesShareMatrix, suggestedTopics]
  );
  const { legendStateMap, handleLegendClick } = useLegendState(initialLabels);
  const legendData = useChartColorPalette(typesShareMatrix, true);

  const colorMap = useMemo(
    () => new Map(legendData.map((item) => [item.label, item.color])),
    [legendData]
  );

  const tooltipFormatter = (params: any): string => {
    return renderToStaticMarkup(<ChartTooltip tooltipFormatterParams={params} />);
  };

  const maxYValue = useMemo(() => {
    if (!topicsDistribution?.length) {
      return 100;
    }

    const maxPercentage = Math.max(...topicsDistribution.map((t) => t.percentage));
    const calculatedMax = Math.ceil(maxPercentage);

    return Math.min(calculatedMax, 100);
  }, [topicsDistribution]);

  const chartOptions = useMemo(
    () => prepareAndGetVerticalBarOptions(tooltipFormatter, typesShareMatrix, colorMap, maxYValue),
    [colorMap, typesShareMatrix]
  );

  const chartStyle = useMemo(() => {
    const barWidth = 150;
    const calculatedWidth = topicsDistribution.length * barWidth;

    return {
      width: `${calculatedWidth}px`,
      height: '235px',
      minWidth: '100%',
    };
  }, [topicsDistribution.length]);

  if (!typesShareMatrix.length) {
    return (
      <div className="stats__panel">
        <div className="page-box__title-action">
          <div className="page-box__title page-box__title--flex">
            {t('contextAnalysis.topicsShare')}
            <CustomTooltip tooltipText="contextAnalysis.topicsShareTooltip" addMargin={false} />
          </div>
        </div>
        <div className="stats-box">
          <div className="stats-box__content">
            <div className="no-data">{t('noDataAvailable')}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stats__panel topic-share">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('contextAnalysis.topicsShare')}
          <CustomTooltip tooltipText="contextAnalysis.topicsShareTooltip" addMargin={false} />
        </div>
      </div>

      <div className="stats-box stats-box--bar-chart">
        <div className="stats-box__content">
          <SimpleBar className="stats-box__scroll-box" style={{ maxWidth: '100%' }} autoHide={false}>
            <CustomBarChart
              customEChartsOption={{ eChartsOption: chartOptions, legendData }}
              legendStateMap={legendStateMap}
              onLegendClick={handleLegendClick}
              chartStyle={chartStyle}
            />
          </SimpleBar>
        </div>
      </div>
    </div>
  );
};

export default TopicShare;
